/**
 * One choice, found by typing — where the list is too long to ship to the page.
 *
 * ---------------------------------------------------------------------------
 * WHY NOT `MultiSelectInput` OR A NATIVE `<select>`
 * ---------------------------------------------------------------------------
 * `MultiSelectInput` filters options the page already holds. That is right for
 * programmes and activities, of which there are tens. It is wrong for the
 * volunteer directory: the API caps a page at 100 (`MAX_PAGE_SIZE`), so "all of
 * them" is not a thing the page can have, and a control that silently searched
 * only the first hundred would be worse than no search at all.
 *
 * So this one does not own the list — the caller does, and refetches as the
 * term changes. `onSearch` fires on every keystroke; debouncing belongs to the
 * caller, which is the only party that knows what it costs to ask again.
 *
 * ---------------------------------------------------------------------------
 * WHY THE OPTIONS ARE BUTTONS
 * ---------------------------------------------------------------------------
 * The same reasoning as `MultiSelectInput`'s checkboxes: a full ARIA combobox
 * has a long keyboard contract, and claiming the role while implementing half
 * of it announces a promise the control does not keep. A list of real buttons
 * is already announced correctly and already works with Tab and Enter; the
 * arrow keys are added on top as roving focus, so focus genuinely moves rather
 * than being described as moving by `aria-activedescendant`.
 *
 * ---------------------------------------------------------------------------
 * WHY `valueLabel` IS A PROP
 * ---------------------------------------------------------------------------
 * Because the list is a search result, the chosen option is usually NOT in it —
 * type a new term and the previous match is gone. The control therefore cannot
 * look its own value up, and a caller that failed to pass the label would get a
 * control that forgot what it was showing. Hence a label beside the value.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './SearchSelectInput.module.css';
import fieldStyles from './Field.module.css';

export interface SearchSelectOption {
  value: string;
  label: string;
  /** A second line under the label — a code, an email, an organisation. */
  hint?: string;
}

export interface SearchSelectInputProps {
  label: string;
  /** The options to show now, which the caller refetches as the term changes. */
  options: SearchSelectOption[];
  /** The chosen value, or `null`. Controlled — the page owns it. */
  value: string | null;
  /** The chosen option's label. Required because `value` is often not in `options`. */
  valueLabel?: string;
  onChange: (value: string | null, option: SearchSelectOption | null) => void;
  /** Fires on every keystroke in the search box. Debounce in the caller. */
  onSearch?: (term: string) => void;
  /** What the closed control says when nothing is chosen. */
  placeholder?: string;
  /**
   * What the search box says. Separate from `placeholder`, because the same
   * words in both read as a bug: the control and the box directly beneath it
   * were both saying "Search by name, code or email".
   */
  searchPlaceholder?: string;
  /** The panel says it is working rather than claiming there are no matches. */
  loading?: boolean;
  emptyMessage?: string;
  error?: string;
  disabled?: boolean;
}

export function SearchSelectInput({
  label,
  options,
  value,
  valueLabel,
  onChange,
  onSearch,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  loading = false,
  emptyMessage = 'No matches.',
  error,
  disabled = false,
}: SearchSelectInputProps) {
  const controlId = useId();
  const errorId = `${controlId}-error`;
  const panelId = `${controlId}-panel`;
  const labelId = `${controlId}-label`;
  const summaryId = `${controlId}-summary`;

  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');

  const wrapRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const panelRef = useRef<HTMLDivElement>(null);

  /*
   * Where the panel sits, in viewport coordinates.
   *
   * ---------------------------------------------------------------------
   * WHY THE PANEL IS PORTALLED RATHER THAN POSITIONED INSIDE THE CONTROL
   * ---------------------------------------------------------------------
   * `Card` sets `overflow: hidden` so its contents are clipped to its rounded
   * corners. An absolutely-positioned panel inside a card is therefore cut off
   * at the card's edge — Parinaam photographed exactly that: the dropdown ended
   * mid-row, slicing a volunteer's name in half. Any ancestor with `overflow`
   * other than `visible` does the same, so there is no arrangement of
   * `position: absolute` that survives being put inside one.
   *
   * So the panel renders into `document.body` and is placed from the control's
   * own bounding rect. That also settles the alignment: its width IS the
   * control's width, rather than a guess that overhung the trigger by 60px.
   */
  const [rect, setRect] = useState<{
    left: number;
    width: number;
    /** Set when the panel hangs below the control. */
    top?: number;
    /** Set instead when it is flipped above it. */
    bottom?: number;
    /** What is left between the control and the edge of the screen. */
    maxHeight: number;
  } | null>(null);

  /*
   * Below the control if there is room, above it if there is not, and never
   * taller than the space it has.
   *
   * Parinaam photographed the version without this: the control sat low on the
   * page, the panel ran off the bottom of the window, and the last volunteer in
   * the list was cut in half by the edge of the screen. Capping the height is
   * what actually fixes that — flipping alone would just move the problem to
   * the top of the window on a short viewport.
   */
  const place = useCallback(() => {
    const shell = shellRef.current;
    if (!shell) return;

    const box = shell.getBoundingClientRect();
    const GAP = 4;
    // Never let the panel touch the very edge; it reads as cut off even when
    // every row inside it is whole.
    const MARGIN = 12;

    const below = window.innerHeight - box.bottom - GAP - MARGIN;
    const above = box.top - GAP - MARGIN;

    /*
     * Below unless below is genuinely too cramped to use.
     *
     * Not "whichever side has more room": a control near the top of a short
     * window can have a few more pixels above it than below, and flipping there
     * throws the panel over the control's own label and the tabs above it for
     * no gain. Below is where a dropdown belongs; above is the exception, taken
     * only when below cannot hold the search box and about three rows.
     */
    const MIN_USEFUL = 240;
    const goBelow = below >= MIN_USEFUL || below >= above;

    setRect({
      left: box.left,
      width: box.width,
      ...(goBelow
        ? { top: box.bottom + GAP }
        : { bottom: window.innerHeight - box.top + GAP }),
      maxHeight: Math.max(160, goBelow ? below : above),
    });
  }, []);

  // Before paint, so the panel never appears at the wrong place for a frame.
  useLayoutEffect(() => {
    if (!open) {
      setRect(null);
      return undefined;
    }

    place();

    // `true` for the capture phase: a scroll inside any ancestor moves the
    // control, and a panel left behind at the old coordinates is worse than one
    // that follows.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  const summary = value === null ? placeholder : (valueLabel ?? 'Selected');

  /*
   * Held in a ref so the effect below can depend on `open` alone.
   *
   * Callers pass an inline arrow, which is a new function every render; an
   * effect that depended on it would clear the search term on every render of
   * the page around it, which looks like the control fighting the typist.
   */
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // The point of the control is that you find things by typing, so the cursor
  // starts in the search box. Closing clears the term so reopening never lands
  // on somebody's half-typed search.
  useEffect(() => {
    if (open) {
      searchRef.current?.focus();
    } else {
      setTerm('');
      onSearchRef.current?.('');
    }
  }, [open]);

  /*
   * Close when the click lands anywhere else. `mousedown` rather than `click`,
   * because a click that starts inside the panel and finishes outside it —
   * selecting the text of an option, say — fires `click` on an ancestor, and
   * closing the panel out from under a drag is maddening.
   */
  useEffect(() => {
    if (!open) return undefined;

    const onDocumentDown = (event: MouseEvent) => {
      const target = event.target as Node;
      // The panel is portalled out of the wrap, so "inside the control" now
      // means either box; checking only the wrap would close the panel on the
      // very click that chose an option.
      if (wrapRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    document.addEventListener('mousedown', onDocumentDown);
    return () => document.removeEventListener('mousedown', onDocumentDown);
  }, [open]);

  function choose(option: SearchSelectOption) {
    onChange(option.value, option);
    setOpen(false);
    triggerRef.current?.focus();
  }

  /** Arrow keys walk the options; focus really moves, so nothing has to lie. */
  function moveFocus(from: number, delta: number) {
    const next = from + delta;
    if (next < 0) {
      searchRef.current?.focus();
      return;
    }
    optionRefs.current[next]?.focus();
  }

  return (
    <div className={fieldStyles.field}>
      <label className={fieldStyles.label} htmlFor={controlId} id={labelId}>
        {label}
      </label>

      <div
        className={styles.wrap}
        ref={wrapRef}
        onKeyDown={(event) => {
          if (event.key === 'Escape' && open) {
            event.stopPropagation();
            setOpen(false);
            triggerRef.current?.focus();
          }
        }}
      >
        <div
          ref={shellRef}
          className={[
            styles.shell,
            error ? styles.shellError : '',
            disabled ? styles.shellDisabled : '',
            open ? styles.shellOpen : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          <button
            type="button"
            id={controlId}
            ref={triggerRef}
            className={styles.trigger}
            disabled={disabled}
            aria-expanded={open}
            aria-controls={open ? panelId : undefined}
            aria-describedby={error ? errorId : undefined}
            // The label AND the current value, which is what a native select
            // announces. A `<label for>` on a button replaces its text rather
            // than adding to it.
            aria-labelledby={`${labelId} ${summaryId}`}
            onClick={() => setOpen((wasOpen) => !wasOpen)}
          >
            <span
              id={summaryId}
              className={value === null ? styles.placeholder : styles.summary}
            >
              {summary}
            </span>
            <svg className={styles.chevron} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
              <path d="M4 6 L8 10 L12 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          {value !== null && !disabled && (
            <button
              type="button"
              className={styles.clear}
              onClick={() => {
                onChange(null, null);
                triggerRef.current?.focus();
              }}
              aria-label={`Clear ${label}`}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                <path d="M3.5 3.5 L10.5 10.5 M10.5 3.5 L3.5 10.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>

        {open && rect
          ? createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            id={panelId}
            style={{
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              width: rect.width,
              maxHeight: rect.maxHeight,
            }}
          >
            <input
              ref={searchRef}
              className={styles.search}
              type="text"
              value={term}
              placeholder={searchPlaceholder}
              aria-label={`Search ${label}`}
              onChange={(event) => {
                setTerm(event.target.value);
                onSearch?.(event.target.value);
              }}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  optionRefs.current[0]?.focus();
                }
              }}
            />

            {loading ? (
              <p className={styles.note}>Searching…</p>
            ) : options.length === 0 ? (
              <p className={styles.note}>{emptyMessage}</p>
            ) : (
              <ul className={styles.list}>
                {options.map((option, index) => (
                  <li key={option.value}>
                    <button
                      type="button"
                      ref={(element) => {
                        optionRefs.current[index] = element;
                      }}
                      className={[
                        styles.option,
                        option.value === value ? styles.optionChosen : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                      onClick={() => choose(option)}
                      onKeyDown={(event) => {
                        if (event.key === 'ArrowDown') {
                          event.preventDefault();
                          moveFocus(index, 1);
                        } else if (event.key === 'ArrowUp') {
                          event.preventDefault();
                          moveFocus(index, -1);
                        }
                      }}
                    >
                      <span className={styles.optionLabel}>{option.label}</span>
                      {option.hint ? <span className={styles.optionHint}>{option.hint}</span> : null}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>,
          document.body
            )
          : null}
      </div>

      {error ? (
        <p className={fieldStyles.error} id={errorId} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
